import { create } from "zustand";
import { VybeAuthState, VybePresenceSelf, VybeUser } from "./types";
import * as api from "./api";
import { tryGetPlatformConfig } from "@/platform/platform-context";
import type { ExternalAuthAdapter } from "@/platform/types";

const STORAGE_KEY = "vybe-auth";

interface Persisted {
  token: string;
  user: VybeUser;
  sessionStartedAt?: number;
}

function save(data: Persisted | null) {
  try {
    if (data) localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore storage failures */
  }
}

function read(): Persisted | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Persisted) : null;
  } catch {
    return null;
  }
}

/** Sentinel for "the user closed the Google chooser" — surfaced as no error, not a red line. */
const CANCELLED = "cancelled";

/** How long the desktop waits on the browser round-trip before releasing the card. */
const GOOGLE_SIGNIN_TIMEOUT_MS = 5 * 60_000;

/**
 * The external-auth adapter, when this shell has one. Read lazily and defensively: the store is also
 * imported by tests and by code paths that run before PlatformProvider mounts.
 */
function externalAuthAdapter(): ExternalAuthAdapter | undefined {
  return tryGetPlatformConfig()?.externalAuth;
}

/** Message the CRM bridge page posts back to this window (proposal 71). */
interface GoogleBridgeMessage {
  type: "vybe-google-signin";
  code?: string | null;
  error?: string | null;
}

/**
 * Opens the CRM Google flow in a popup and resolves with the single-use handoff code the bridge page
 * posts back. The sender origin is checked against the configured CRM origin, so no other frame can feed
 * us a code. When the popup can't be opened (blocker, or a shell that has no window.open), we navigate the
 * whole terminal into the flow instead — the CRM then returns via `#vt-handoff=…`, which
 * `parseLaunchFragment` already consumes at boot.
 */
function awaitGoogleHandoff(): Promise<string> {
  const origin = api.crmOrigin();
  if (!origin) return Promise.reject(new Error("Vybe Trader accounts are not configured"));

  // Shells that can't host the popup (desktop: Google refuses embedded webviews) supply an adapter that
  // runs the flow in the system browser and returns the code over a deep link.
  const external = externalAuthAdapter();
  if (external) return external.signIn(api.googleSignInUrl(external.flow), GOOGLE_SIGNIN_TIMEOUT_MS);

  const popup =
    typeof window === "undefined"
      ? null
      : window.open(api.googleSignInUrl(), "vybe-google-signin", "width=480,height=640,menubar=no,toolbar=no");

  if (!popup) {
    window.location.assign(api.googleSignInUrl());
    // The navigation above ends this document; nothing after it ever runs.
    return new Promise<string>(() => {});
  }

  return new Promise<string>((resolve, reject) => {
    let done = false;
    const finish = (fn: () => void) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMessage);
      clearInterval(closedPoll);
      try {
        popup.close();
      } catch {
        /* already closed */
      }
      fn();
    };

    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== origin) return;
      const data = ev.data as GoogleBridgeMessage | null;
      if (!data || data.type !== "vybe-google-signin") return;
      if (data.code) finish(() => resolve(data.code!));
      else
        finish(() =>
          reject(new Error(data.error === "denied" ? "Google sign-in was declined" : "Google sign-in failed")),
        );
    };

    // The popup carries no code when the user simply closes it, so poll for that instead of hanging.
    const closedPoll = window.setInterval(() => {
      if (popup.closed) finish(() => reject(new Error(CANCELLED)));
    }, 500);

    window.addEventListener("message", onMessage);
  });
}

export const useVybeAuthStore = create<VybeAuthState>((set, get) => ({
  token: null,
  user: null,
  onlineCount: 0,
  presenceConnected: false,
  sessionStartedAt: null,
  status: "signed-out",
  error: null,
  pendingEmail: null,

  requestCode: async (email: string) => {
    set({ error: null });
    try {
      await api.requestCode(email.trim().toLowerCase());
      set({ status: "code-sent", pendingEmail: email.trim().toLowerCase() });
      return true;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to send code" });
      return false;
    }
  },

  verifyCode: async (code: string) => {
    const email = get().pendingEmail;
    if (!email) {
      set({ error: "Enter your email first" });
      return false;
    }
    set({ error: null });
    try {
      const { token, user } = await api.verifyCode(email, code);
      const sessionStartedAt = Date.now();
      save({ token, user, sessionStartedAt });
      set({ token, user, sessionStartedAt, status: "signed-in", pendingEmail: null });
      return true;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Verification failed" });
      return false;
    }
  },

  signInWithGoogle: async () => {
    set({ error: null, status: "google-pending" });
    try {
      const code = await awaitGoogleHandoff();
      const { token, user } = await api.redeemHandoff(code);
      const sessionStartedAt = Date.now();
      save({ token, user, sessionStartedAt });
      set({ token, user, sessionStartedAt, status: "signed-in", pendingEmail: null });
      return true;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Google sign-in failed";
      // A user closing the chooser isn't an error worth shouting about — drop back to the card silently.
      set({ status: "signed-out", error: message === CANCELLED ? null : message });
      return false;
    }
  },

  logout: async () => {
    const token = get().token;
    if (token) await api.logout(token);
    save(null);
    set({
      token: null,
      user: null,
      status: "signed-out",
      onlineCount: 0,
      presenceConnected: false,
      sessionStartedAt: null,
    });
  },

  expireSession: (reason?: string) => {
    // Same teardown as logout but WITHOUT the server round-trip (the token is already
    // dead) and with an error to explain the re-prompt.
    save(null);
    set({
      token: null,
      user: null,
      status: "signed-out",
      onlineCount: 0,
      presenceConnected: false,
      sessionStartedAt: null,
      error: reason ?? "Your session expired — please sign in again.",
    });
  },

  load: () => {
    const stored = read();
    if (stored) {
      // A fresh app load IS a fresh session: the stored timestamp survives every
      // restart, so reusing it made the identity card count months ("Session 8766h")
      // instead of this run. Restart the clock and persist it for the next reader.
      const sessionStartedAt = Date.now();
      save({ ...stored, sessionStartedAt });
      set({
        token: stored.token,
        user: stored.user,
        sessionStartedAt,
        status: "signed-in",
      });
    }
  },

  setOnlineCount: (n: number) => set({ onlineCount: n }),

  setPresenceConnected: (connected: boolean) => set({ presenceConnected: connected }),

  applyPresenceSelf: (self: VybePresenceSelf) => {
    const prev = get().user;
    if (!prev) return;
    const user: VybeUser = {
      ...prev,
      email: self.email || prev.email,
      displayName: self.displayName ?? prev.displayName,
      roles: self.roles ?? prev.roles,
      plan: self.plan ?? prev.plan,
    };
    save({ token: get().token!, user, sessionStartedAt: get().sessionStartedAt ?? Date.now() });
    set({ user });
  },
}));

/**
 * True when the signed-in user carries the "Admin" role. Client-side UX gating
 * only — NOT a security boundary.
 */
export const useIsAdmin = () =>
  useVybeAuthStore((s) => !!s.user?.roles?.includes("Admin"));

/**
 * True when the signed-in user is on the "Desk" plan. Client-side UX gating
 * only — NOT a security boundary.
 */
export const useIsDeskPlan = () =>
  useVybeAuthStore((s) => s.user?.plan === "Desk");

/** Current bearer token for LLM requests (or null). Safe to call outside React. */
export const getVybeToken = (): string | null => useVybeAuthStore.getState().token;

/**
 * Fragment contract v2 (proposal 52): `#vt-handoff=<code>&connect=<token>`. The CRM onboarding
 * autopilot puts BOTH artifacts in the URL fragment (never query params — the fragment never reaches
 * server logs). Parsed exactly once, and the whole fragment is scrubbed from the address bar
 * immediately — even before redemption, so a failed redeem never leaves either artifact visible.
 */
let fragmentParsed = false;
let fragmentHandoffCode: string | null = null;
let fragmentConnectToken: string | null = null;

function parseLaunchFragment(): void {
  if (fragmentParsed) return;
  fragmentParsed = true;
  try {
    if (typeof window === "undefined" || !window.location?.hash) return;
    const params = new URLSearchParams(window.location.hash.slice(1));
    const handoff = params.get("vt-handoff");
    const connect = params.get("connect");
    if (!handoff && !connect) return;
    fragmentHandoffCode = handoff;
    fragmentConnectToken = connect;
    // Scrub the whole fragment — the connect token carries trading credentials.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  } catch {
    /* ignore malformed fragments */
  }
}

/**
 * One-shot consumer for the fragment-borne connect token (`#…&connect=<token>`), used by the
 * accounts deep-link hook. Returns null after the first take or when absent.
 */
export function takeFragmentConnectToken(): string | null {
  parseLaunchFragment();
  const token = fragmentConnectToken;
  fragmentConnectToken = null;
  return token;
}

// Resolves once the boot-time auth hydration (handoff redeem or localStorage load) has settled —
// the onboarding flow redeems the session FIRST, then connects the trading account.
let resolveAuthSettled: (() => void) | null = null;
const authSettled = new Promise<void>((resolve) => {
  resolveAuthSettled = resolve;
});

/** Awaits boot-time auth settlement, with a 15s safety valve so callers can never hang. */
export function whenVybeAuthSettled(): Promise<void> {
  return Promise.race([
    authSettled,
    new Promise<void>((resolve) => setTimeout(resolve, 15_000)),
  ]);
}

/** Initialize from localStorage once at app start; redeems a CRM SSO handoff code if present. */
export const initializeVybeAuthStore = () => {
  parseLaunchFragment();
  const code = fragmentHandoffCode;
  fragmentHandoffCode = null;
  if (!code) {
    useVybeAuthStore.getState().load();
    resolveAuthSettled?.();
    return;
  }
  // Non-blocking: redeem in the background, fall back to the stored session on failure.
  void api
    .redeemHandoff(code)
    .then(({ token, user }) => {
      const sessionStartedAt = Date.now();
      save({ token, user, sessionStartedAt });
      useVybeAuthStore.setState({
        token,
        user,
        sessionStartedAt,
        status: "signed-in",
        pendingEmail: null,
        error: null,
      });
    })
    .catch(() => {
      useVybeAuthStore.getState().load();
    })
    .finally(() => {
      resolveAuthSettled?.();
    });
};
